import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchPromoterSalesPayoutTransactions,
  fetchPromoterVenues,
} from "../../services/promoterRequestService";
import Loader from "../child/Loder";
import DengerAlert from "../child/Alert/DengerAlert";
import Breadcrumb from "../Breadcrumb";
import MasterLayout from "../../masterLayout/MasterLayout";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatApiDate } from "../../utils/dateHelper";

const PromoterBottleService = () => {
  const { id } = useParams();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [hostId, setHostId] = useState("");
  const [venueOptions, setVenueOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const toId = (value) => {
    if (value == null || value === "") return "";
    if (typeof value === "object") {
      return String(value._id || value.id || "").trim();
    }
    return String(value).trim();
  };

  const getRowHostId = (row) =>
    toId(row?.hostId) ||
    toId(row?.host) ||
    toId(row?.hostDetail?.hostId) ||
    toId(row?.hostDetail) ||
    toId(row?.venueHostId);

  const getRowVenueName = (row) =>
    String(
      row?.venueName ||
        row?.clubName ||
        row?.hostBusinessName ||
        row?.businessName ||
        row?.hostName ||
        row?.host?.businessName ||
        row?.host?.fullName ||
        ""
    ).trim();

  const fetchVenueOptions = async () => {
    if (!id) return;
    try {
      const [venuesRes, txnRes] = await Promise.all([
        fetchPromoterVenues(id, {
          page: 1,
          limit: 9999,
          search: "",
          status: "",
          sortBy: "venueName",
          sortOrder: "asc",
        }),
        fetchPromoterSalesPayoutTransactions(id, {
          page: 1,
          limit: 9999,
          status: "",
          servicesType: "",
          search: "",
          startDate: "",
          endDate: "",
        }),
      ]);

      const uniqueVenues = new Map();
      const addVenue = (value, label) => {
        if (!value) return;
        const nextLabel = String(label || "").trim();
        if (!nextLabel || nextLabel.toLowerCase() === "n/a") return;
        uniqueVenues.set(value, { value, label: nextLabel });
      };

      (venuesRes?.data?.data || []).forEach((row) => {
        addVenue(getRowHostId(row) || toId(row?._id), getRowVenueName(row));
      });

      (txnRes?.data?.data || []).forEach((row) => {
        addVenue(getRowHostId(row), getRowVenueName(row));
      });

      setVenueOptions(
        Array.from(uniqueVenues.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      );
    } catch (error) {
      console.error("Venue filter fetch error:", error);
      setVenueOptions([]);
    }
  };

  const fetchTransactions = async () => {
    if (!id) return;

    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";

    if (
      formattedStartDate &&
      formattedEndDate &&
      moment(formattedStartDate).isAfter(moment(formattedEndDate))
    ) {
      setErrorMessage("Start date cannot be greater than end date");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const payload = {
        page: currentPage,
        limit: itemsPerPage,
        hostId: hostId || "",
        status: statusFilter,
        servicesType: serviceTypeFilter,
        search: searchQuery,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      };

      const response = await fetchPromoterSalesPayoutTransactions(id, payload);

      if (response?.data?.status) {
        setTransactions(response?.data?.data || []);
        setTotalItems(response?.data?.total || 0);
        setTotalPages(response?.data?.totalPages || 1);
      } else {
        setTransactions([]);
        setTotalItems(0);
        setTotalPages(1);
        setErrorMessage(
          response?.data?.message ||
            "Unable to fetch promoter transaction history."
        );
      }
    } catch (error) {
      console.error("Promoter transaction history error:", error);
      setTransactions([]);
      setTotalItems(0);
      setTotalPages(1);
      setErrorMessage(
        error?.response?.data?.message ||
          "Something went wrong while fetching transaction history."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVenueOptions();
  }, [id]);

  useEffect(() => {
    fetchTransactions();
  }, [
    id,
    currentPage,
    itemsPerPage,
    statusFilter,
    serviceTypeFilter,
    hostId,
    searchQuery,
    startDate,
    endDate,
  ]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const getPaginationRange = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5];
    }

    if (currentPage >= totalPages - 2) {
      return [
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
    ];
  };

  const getMembersCount = (item) => {
    const value =
      item?.memberCount ??
      item?.members ??
      item?.member ??
      item?.booking?.memberCount ??
      item?.booking?.members;
    if (value === null || value === undefined || value === "") return "N/A";
    if (Array.isArray(value)) return value.length;
    return value;
  };

  const getTicketNames = (item) => {
    const value =
      item?.ticketName ??
      item?.ticket_name ??
      item?.booking?.ticketName ??
      item?.bookingData?.ticketName ??
      item?.ticket?.ticketName;
    if (value === null || value === undefined || value === "") return "N/A";
    return String(value);
  };

  const formatServiceType = (value) => {
    if (!value) return "N/A";

    const normalized = String(value).toLowerCase().replace(/[\s_-]/g, "");
    if (normalized === "overallvenue" || normalized === "overall") {
      return "Venue";
    }
    if (normalized === "bottleservice") return "Bottle Service";
    if (normalized === "vipentry" || normalized === "vipentery") {
      return "VIP Entry";
    }

    return value
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const formatStatus = (value) => {
    if (!value) return "N/A";
    const normalized = String(value).toLowerCase();
    if (normalized === "completed" || normalized === "paid" || normalized === "success") {
      return "Paid";
    }
    if (normalized === "pending") return "Pending";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "paid":
      case "success":
        return "bg-success-focus text-success-600 border border-success-main";
      case "pending":
        return "bg-warning-focus text-warning-600 border border-warning-main";
      default:
        return "bg-neutral-200 text-secondary-light border";
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <MasterLayout>
        <Breadcrumb
          title2="Promoter Management"
          link2="/promoter"
          title3="View Promoter"
          link3={`/promoter/view-promoter/${id}`}
          title="Service History"
        />
        <Loader />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title2="Promoter Management"
        link2="/promoter"
        title3="View Promoter"
        link3={`/promoter/view-promoter/${id}`}
        title="Service History"
      />

      <div className="card h-100 p-0 radius-12">
        {errorMessage && (
          <DengerAlert
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )}

        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">
              Show
            </span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10 items per page</option>
              <option value="20">20 items per page</option>
              <option value="50">50 items per page</option>
              <option value="100">100 items per page</option>
            </select>

            <form
              className="navbar-search"
              style={{ position: "relative", minWidth: "391px" }}
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="text"
                className="bg-base h-40-px pe-40"
                name="search"
                placeholder="Search by user name,ticket name or venue name"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: "100%",
                  minWidth: "391px",
                  paddingRight: searchQuery ? "5rem" : "2.5rem",
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="icon"
                style={{
                  position: "absolute",
                  right: searchQuery ? "3.25rem" : "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#999",
                  pointerEvents: "none",
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "30%",
                    transform: "translateY(-50%)",
                    fontSize: "1.25rem",
                    color: "#000",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </form>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={hostId}
              onChange={(e) => {
                setHostId(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Venue</option>
              {venueOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={serviceTypeFilter}
              onChange={(e) => {
                setServiceTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Service</option>
              <option value="overallVenue">Venue</option>
              <option value="Booth">Booth</option>
              <option value="Event">Event</option>
              <option value="VIP Entry">VIP Entry</option>
              <option value="Table">Table</option>
              <option value="BottleService">Bottle Service</option>
              <option value="Scanned">Scanned</option>
            </select>

            <div style={{ position: "relative", maxWidth: "200px" }}>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  if (
                    date &&
                    endDate &&
                    moment(date).isAfter(moment(endDate), "day")
                  ) {
                    setEndDate(null);
                  }
                  setCurrentPage(1);
                }}
                preventOpenOnFocus={false}
                onKeyDown={(e) => e.preventDefault()}
                className="form-control no-caret"
                placeholderText="Start Date"
                dateFormat="yyyy-MM-dd"
              />
              {startDate && (
                <button
                  type="button"
                  onClick={() => {
                    setStartDate(null);
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    fontSize: "1.25rem",
                    color: "#000",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </div>

            <div style={{ position: "relative", maxWidth: "200px" }}>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  setEndDate(date);
                  setCurrentPage(1);
                }}
                minDate={startDate || undefined}
                preventOpenOnFocus={false}
                onKeyDown={(e) => e.preventDefault()}
                className="form-control no-caret"
                placeholderText="End Date"
                dateFormat="yyyy-MM-dd"
              />
              {endDate && (
                <button
                  type="button"
                  onClick={() => {
                    setEndDate(null);
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    fontSize: "1.25rem",
                    color: "#000",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Service</th>
                  <th>User Name</th>
                  <th>Members</th>
                  <th>Ticket Name</th>
                  <th>Venue Name</th>
                  <th>Promoter Commission</th>
                   <th>Sales Amount</th>
                  <th>Status</th>
                  <th>Booking Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length > 0 ? (
                  transactions.map((item, index) => (
                    <tr key={item._id || index}>
                      <td>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td>
                        {formatApiDate(item.createdAt)}
                      </td>
                      <td>
                        <span className="fw-semibold">
                          {formatServiceType(item.servicesType)}
                        </span>
                      </td>
                      <td>{item.userFullName || "N/A"}</td>
                      <td>{getMembersCount(item)}</td>
                      <td>{getTicketNames(item)}</td>
                      <td>
                        {item.venueName ||
                          item.hostBusinessName ||
                          "N/A"}
                      </td>
                      <td className="fw-semibold">
                        {/* {item.currency
                          ? item.currency.toUpperCase()
                          : "$"}{" "} */}$
                        {Number(item.amount || 0).toLocaleString()}
                      </td>
                      <td className="fw-semibold">
                        {/* {item.currency
                          ? item.currency.toUpperCase()
                          : "$"}{" "} */}
                          $
                        {Number(item.totalBaseAmount || 0).toLocaleString()}
                      </td>
                      <td>
                        <span
                          className={`px-24 py-4 radius-4 fw-medium text-sm ${getStatusClass(
                            item.status
                          )}`}
                        >
                          {formatStatus(item.status)}
                        </span>
                      </td>
                      <td>
                        {formatApiDate(
                          item.bookingStartDate ||
                            item.bookingDate ||
                            item.bookingEndDate
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center py-5">
                      {loading
                        ? "Loading transaction history..."
                        : "No transaction history found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              {totalItems === 0 ? (
                <>No entry to show</>
              ) : (
                <>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems} entries
                </>
              )}
            </span>

            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  disabled={currentPage === 1 || loading}
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>

              {getPaginationRange().map((pageNum) => (
                <li key={pageNum} className="page-item">
                  <button
                    type="button"
                    disabled={loading}
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                      currentPage === pageNum
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-200"
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${
                  currentPage === totalPages || totalPages === 0
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  type="button"
                  disabled={
                    currentPage === totalPages || totalPages === 0 || loading
                  }
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default PromoterBottleService;
